'use client';

import React, { isValidElement, useEffect, useState } from "react";
import {
    Backdrop,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
    useTheme
} from "@mui/material";
import Help from "@mui/icons-material/Help";
import {IUIDialogProps} from "./UIDialog.types";
import {Form} from "../Form/Form";
import {FieldValues} from "react-hook-form";

export function UIDialog<T extends FieldValues>(props: IUIDialogProps<T>) {

    // STATE
    const [open, setOpen] = useState(props.isOpen !== undefined ? props.isOpen : false);

    // HOOKS
    const theme = useTheme();

    useEffect(() => {
        if (props.isOpen !== undefined) {
            setOpen(props.isOpen);
        }

        if(props.isOpen && props.onOpen) {
            props.onOpen();
        }
    }, [props.isOpen]);

    // HANDLERS
    const handleClose = () => {
        if (props.onClose) {
            props.onClose();
        }
        if (props.onCancel) {
            props.onCancel();
        }
        setOpen(false);
    };

    const handleOpen = () => {
        if (props.onOpen) {
            props.onOpen();
        }
        setOpen(true);
    };

    // effect
    useEffect(() => {
        if (props.triggerClose) {
            handleClose();
        }
    }, [props.triggerClose]);


    const BUTTON = props.buttonProps ? <Button {...props.buttonProps} onClick={handleOpen} />
        :
        props.buttonText && props.buttonText.length > 0 ? (
        <Button
            disabled={props.disabled ?? false}
            onClick={handleOpen}
            variant={props.contained ? "contained" : "text"}
            startIcon={props.buttonIcon || undefined}
            style={{
                display: "flex",
                alignItems: "center",
                padding: props.contained ? "auto" : 0,
            }}>
            {props.buttonText ?? ''}
        </Button>
        ) : (
            <IconButton sx={{ width: 'max-content' }} disabled={props.disabled ?? false} onClick={handleOpen} style={{ cursor: "pointer" }}>
                {props.buttonIcon}
            </IconButton>
        );

    const generateContent = () => {

        const INNER = (
            <>
                {props.help ? (
                    <Tooltip title={props.help}>
                        <Box position={"absolute"} top={12} right={12} style={{ cursor: "help" }}>
                            <Help />
                        </Box>
                    </Tooltip>
                ) : <></>}
                <Box
                    height={"0%"}
                    position={"absolute"}
                    left={0}
                    bottom={0}
                    width={"18px"}
                    bgcolor={theme.palette.background.default}
                />
                <Box p={"12px"}
                     display={'flex'}
                     maxHeight={'90vh'}
                     flexDirection={'column'}
                >
                    <DialogTitle style={{ marginBottom: !props.subtitle ? "12px" : undefined , paddingBottom: props.subtitle ? 0 : undefined}}>
                        {props.avatar && (
                            <Box height={"30px"} width={"30px"} mr={"12px"}>
                                {props.avatar}
                            </Box>
                        )}
                        {props.title}
                    </DialogTitle>
                    <Box pl={'24px'}>
                        { props.subtitle && isValidElement(props.subtitle) ? props.subtitle : <Typography variant="subtitle2">{props.subtitle}</Typography> }
                    </Box>
                    <DialogContent
                        sx={props.dialogContentStyles}>
                        <Box overflow={props.overflow || "none"}>{props.content}</Box>
                    </DialogContent>
                    <DialogActions sx={{ mt: "16px", gap: '6px' }}>
                        {/*<Box display={"flex"} alignItems={"center"}>*/}
                        {props.action || null}
                        <Button onClick={handleClose} disabled={props.loading} variant={'text'} color={props.cancelButtonColor ? props.cancelButtonColor : 'info'}>
                            {props.cancelButtonText || 'Cancel'}
                        </Button>
                        {/*</Box>*/}
                    </DialogActions>
                </Box>
            </>
        )

        if(props.form && props.form.onSubmit) {
            return (
                <Form {...props.form} onSubmit={props.form.onSubmit}>
                    {INNER}
                </Form>
            )
        } else {
            return INNER
        }
    }

    return (
        <>
            {(props.isOpen === null || props.isOpen === undefined) && BUTTON}
            {props.hasButton && BUTTON}

            <Dialog
                disableEscapeKeyDown={props.loading ?? false}
                {...!props.disableBackdropClick && {onClose: handleClose}}
                open={open}
                maxWidth={props.size}
                fullWidth={!!props.size}
                sx={{

                }}
            >
                {generateContent()}
            </Dialog>
        </>
    );
}

export default UIDialog;
